import * as React from 'react'
import { cn } from '@/lib/utils'

export type HTMLTag =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'aside'
  | 'small'

export type AlignType = 'left' | 'center' | 'right'

type OneWeight =
  | { medium?: true; semibold?: never; bold?: never }
  | { medium?: never; semibold?: true; bold?: never }
  | { medium?: never; semibold?: never; bold?: true }
  | { medium?: never; semibold?: never; bold?: never }

type WeightKey = 'normal' | 'medium' | 'semibold' | 'bold'
const resolveWeight = (p: OneWeight): WeightKey =>
  p.medium ? 'medium' : p.semibold ? 'semibold' : p.bold ? 'bold' : 'normal'

type BaseProps = Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'color'> &
  OneWeight & {
    id?: string
    children?: React.ReactNode
    className?: string
    lowercase?: boolean
    uppercase?: boolean
    color?: string
    as?: HTMLTag
    htmlFor?: string
    wrap?: React.CSSProperties['textWrap']
    isParagraph?: boolean
    align?: AlignType
  }

type Role =
  | 'heading-1'
  | 'heading-2'
  | 'heading-3'
  | 'heading-4'
  | 'heading-5'
  | 'heading-6'
  | 'title-1'
  | 'title-2'
  | 'body-1'
  | 'body-2'
  | 'caption-1'
  | 'caption-2'
  | 'overline'
  | 'large'
  | 'small'
  | 'muted'

const DEFAULT_TAG: Record<Role, HTMLTag> = {
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'heading-4': 'h4',
  'heading-5': 'h5',
  'heading-6': 'h6',
  'title-1': 'span',
  'title-2': 'span',
  'body-1': 'span',
  'body-2': 'span',
  'caption-1': 'span',
  'caption-2': 'span',
  overline: 'span',
  large: 'div',
  small: 'small',
  muted: 'p',
}

const ROLE_CLASS: Record<Role, string> = {
  'heading-1': 'scroll-m-20 text-4xl font-extrabold tracking-tight text-balance leading-none',
  'heading-2': 'scroll-m-20 text-3xl font-semibold tracking-tight first:mt-0 leading-none',
  'heading-3': 'scroll-m-20 text-2xl font-semibold tracking-tight leading-none',
  'heading-4': 'scroll-m-20 text-xl font-semibold tracking-tight leading-none',
  'heading-5': 'scroll-m-20 text-lg font-semibold tracking-tight leading-none',
  'heading-6': 'scroll-m-20 text-base font-semibold tracking-tight leading-none',
  'title-1': 'text-lg font-semibold leading-none',
  'title-2': 'text-base font-semibold leading-none',
  'body-1': 'leading-7',
  'body-2': 'text-sm leading-6',
  'caption-1': 'text-sm text-muted-foreground',
  'caption-2': 'text-xs text-muted-foreground',
  overline: 'text-xs font-semibold uppercase tracking-wider text-muted-foreground',
  large: 'text-lg font-semibold',
  small: 'text-sm leading-none font-medium',
  muted: 'text-muted-foreground text-sm',
}

const WEIGHT_CLASS: Record<WeightKey, string> = {
  normal: '',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
}

const ALIGN_CLASS: Record<AlignType, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}

function makeTypography<R extends Role>(role: R) {
  const Comp = React.forwardRef<HTMLElement, BaseProps>(function Typography(
    { as, isParagraph, className, wrap, children, align, lowercase, uppercase, color, ...rest },
    ref
  ) {
    const weight = resolveWeight(rest)
    const Tag = (isParagraph ? 'p' : (as ?? DEFAULT_TAG[role])) as React.ElementType

    const { medium, semibold, bold, style, ...dom } = rest as any

    const cls = cn(
      ROLE_CLASS[role],
      WEIGHT_CLASS[weight],
      color,
      align ? ALIGN_CLASS[align] : null,
      lowercase ? 'lowercase' : null,
      uppercase ? 'uppercase' : null,
      className
    )

    return (
      <Tag ref={ref as any} className={cls} style={{ ...(style as any), textWrap: wrap }} {...dom}>
        {children}
      </Tag>
    )
  })

  Comp.displayName = role
    .split('-')
    .map((s) => s[0].toUpperCase() + s.slice(1))
    .join('')

  return Comp
}

export const Heading1 = makeTypography('heading-1')
export const Heading2 = makeTypography('heading-2')
export const Heading3 = makeTypography('heading-3')
export const Heading4 = makeTypography('heading-4')
export const Heading5 = makeTypography('heading-5')
export const Heading6 = makeTypography('heading-6')

export const Title1 = makeTypography('title-1')
export const Title2 = makeTypography('title-2')

export const Body1 = makeTypography('body-1')
export const Body2 = makeTypography('body-2')

export const Caption1 = makeTypography('caption-1')
export const Caption2 = makeTypography('caption-2')

export const Overline = makeTypography('overline')
